/**
 * T-ONE Technologies App
 * https://github.com/facebook/react-native
 *
 * @format
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { StatusBar, View, ActivityIndicator, StyleSheet, SafeAreaView, Platform, BackHandler, ToastAndroid, PanResponder, Alert } from 'react-native';
import DeviceInfo from 'react-native-device-info';
import { useWebStatusBarColor } from './web/statusBarColor';
import { AuthProvider, useAuth } from './src/contexts/AuthContext';
import { ChatProvider } from './src/contexts/ChatContext';
import { HiddenAccountsProvider } from './src/contexts/HiddenAccountsContext';
import { PINProvider, usePIN } from './src/contexts/PINContext';
import { SnackbarProvider } from './src/contexts/SnackbarContext';
import {
  BiometricRecommendationScreen,
  PasswordRecommendationScreen,
} from './src/screens/Intro/SecurityRecommendationScreens';
import { SignUpScreen } from './src/screens/SignUpScreen';
import { LoginScreen } from './src/screens/Auth/LoginScreen';
import { ForgotPasswordScreen } from './src/screens/Auth/ForgotPasswordScreen';
import DashboardV2Screen from './src/screens/Dashboard/DashboardV2Screen';
import ReportsScreen from './src/screens/ReportsScreen';
import SupportFlowScreen, { SupportInitialRoute } from './src/screens/Support/SupportFlowScreen';
import ProfileScreen from './src/screens/Profile/ProfileScreen';
import ChangePasswordScreen from './src/screens/Profile/ChangePasswordScreen';
import ChangePINScreen from './src/screens/Profile/ChangePINScreen';
import UserSessionsScreen from './src/screens/Profile/UserSessionsScreen';
import PDFPreviewScreen from './src/screens/PDFPreviewScreen';
import PINSetupScreen from './src/screens/Auth/PINSetupScreen';
import PINUnlockScreen from './src/screens/Auth/PINUnlockScreen';
import BalanceDetailScreen from './src/screens/Account/BalanceDetailScreen';
import DepositAccountScreen from './src/screens/Account/DepositAccountScreen';
import InvestmentAccountScreen from './src/screens/Account/InvestmentAccountScreen';
import CardScreen from './src/screens/Card/CardScreen';
import NoCardScreen from './src/screens/Card/NoCardScreen';
import OperationsScreen, { OperationsTab } from './src/screens/Operations/OperationsScreen';
import OpenDepositScreen from './src/screens/Account/Deposit/OpenDepositScreen';
import TopUpDepositScreen from './src/screens/Account/Deposit/TopUpDepositScreen';
import CloseDepositScreen from './src/screens/Account/Deposit/CloseDepositScreen';
import OutflowDepositScreen from './src/screens/Account/Deposit/OutflowDepositScreen';
import CardHistoryScreen from './src/screens/Card/CardHistoryScreen';
import ProductsScreen from './src/screens/Products/ProductsScreen';
import TransactionDetailScreen from './src/screens/Card/TransactionDetailScreen';
import CardInfoScreen from './src/screens/Card/CardInfoScreen';
import CardBlockScreen, { CardBlockMode } from './src/screens/Card/CardBlockScreen';
import TransferSuccessScreen from './src/screens/TransferSuccessScreen';
import QRScannerScreen from './src/screens/QRScannerScreen';
import AllAssetsScreen from './src/screens/Dashboard/AllAssetsScreen';
import PopularTokensScreen from './src/screens/Dashboard/PopularTokensScreen';
import TokenDetailScreen from './src/screens/Dashboard/TokenDetailScreen';
import NotificationsScreen from './src/screens/Notification/NotificationsScreen';
import TransactionHistoryScreen, { type HistoryTab } from './src/screens/Transaction/TransactionHistoryScreen';
import TransactionHistoryDetailScreen from './src/screens/Transaction/TransactionHistoryDetailScreen';
import OrderDetailScreen from './src/screens/Orders/OrderDetailScreen';
import analyticsService from './src/services/analytics';
import { notificationService } from './src/services/notifications';
import { hasLoggedInBefore } from './src/services/auth';
import {
  FirstLoginSecurityStep,
  initializeFirstLoginSecurityStep,
  setFirstLoginSecurityStep,
  shouldSkipBiometricRecommendation,
} from './src/services/firstLoginSecurity';
import { V2Balance, V2CardDetailResponse, V2Document, V2AppBannerResponse, V2HistoryRecord, V2Request, V2Token, V2XStock } from './src/types/v2';
import type { ExchangePreset } from './src/screens/Account/Exchange/ExchangeScreen';
import { presetForBalance } from './src/screens/Account/Exchange/exchangePreset';
import { v2GetCard, v2GetAppBanner, v2GetMyBalances, v2GetMyHistory, v2GetMyRequest } from './src/services/v2/api';
import { COLORS } from './src/theme/themeTokens';
import { createCardDetailLoader } from './src/utils/cardDetailLoader';
import { backLandingFor, backTargetFor } from './src/navigation/backTarget';
import BannerScreen from './src/screens/Banner/BannerScreen';
import ImpersonationBanner from './src/components/ImpersonationBanner';
import { consumeSupportPrefill, setSupportRequestReference } from './src/tools/supportPrefill';
import { INITIAL_USER_SCOPED_STATE, resetUserScopedState, useOnUserGone } from './src/utils/userScopedState';
import { pickTopUpCard } from './src/utils/topUpCard';
import { useIdleLogout } from './web/idleLogout';

/** Balance on a current account (a missing code is a legacy current balance). */
const isCurrentAccountBalance = (b: V2Balance) => (b.account_type_code ?? 'current') === 'current';

type AppScreen = 'loading' | 'login' | 'signup' | 'forgotPassword' | 'main' ;
type TabScreen = 'dashboard' | 'orders' | 'orderDetail' | 'products' | 'history' | 'historyDetail' | 'reports' | 'support' | 'profile' | 'changePassword' | 'changePIN' | 'pinSetup' | 'userSessions' | 'balanceDetail' | 'cardDetail' | 'cardHistory' | 'cardInfo' | 'cardBlock' | 'operations' | 'transactionDetail' | 'transferSuccess' |'allAssets' | 'qrScanner'  | 'totalBalance' | 'popularTokens' | 'tokenDetail' | 'notifications' | 'noCard' | 'openDeposit' | 'depositAccount' | 'investmentAccount' | 'topupDeposit' | 'closeDeposit' | 'outflowDeposit';

/**
 * Compare two semver version strings (e.g. "1.2.0" vs "1.3.1").
 * Returns: -1 if a < b, 0 if a == b, 1 if a > b.
 */
function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const na = pa[i] ?? 0;
    const nb = pb[i] ?? 0;
    if (na < nb) return -1;
    if (na > nb) return 1;
  }
  return 0;
}

function AppContent(): React.JSX.Element {
  const [currentScreen, setCurrentScreen] = useState<AppScreen>('loading');
  const [currentTab, setCurrentTab] = useState<TabScreen>('dashboard');
  const [supportInitialRoute, setSupportInitialRoute] = useState<SupportInitialRoute>('chooser');
  const [checkedFirstLogin, setCheckedFirstLogin] = useState(false);
  const [firstLoginSecurityStep, setFirstLoginSecurityStepState] =
    useState<FirstLoginSecurityStep | null>(null);
  const [pdfDocument, setPdfDocument] = useState<V2Document | null>(INITIAL_USER_SCOPED_STATE.pdfDocument);
  const [selectedBalance, setSelectedBalance] = useState<V2Balance | null>(INITIAL_USER_SCOPED_STATE.selectedBalance);
  /** ID of the investment account currently open on DepositAccountScreen.
   *  Replaces the previous selectedDepositId — new code routes by account
   *  (works for both new deposits and legacy investment accounts that
   *  have no v1_2_deposits row). */
  const [selectedInvestmentAccountId, setSelectedInvestmentAccountId] = useState<string | null>(INITIAL_USER_SCOPED_STATE.selectedInvestmentAccountId);
  /** ID of the investment (T-Fund-like) account currently open on
   *  InvestmentAccountScreen. Отдельно от selectedInvestmentAccountId —
   *  тот тянет DepositAccountScreen для account_type='deposit'
   *  (классические срочные), а этот — новый InvestmentAccountScreen
   *  для account_type='investment' (T-Fund-like). Splitting driven by
   *  balance.account_type_code в handleOpenInvestmentDeposit. */
  const [selectedTFundAccountId, setSelectedTFundAccountId] = useState<string | null>(INITIAL_USER_SCOPED_STATE.selectedTFundAccountId);
  const [investmentAccountOrigin, setInvestmentAccountOrigin] = useState<TabScreen>('dashboard');
  /** Origin tab for OpenDepositScreen's back button. Defaults to
   *  'products' (legacy PDF-preview entry); set to 'depositAccount'
   *  when the user comes in via the "Open new" action there. */
  const [openDepositOrigin, setOpenDepositOrigin] = useState<TabScreen>('products');
  const [selectedCardBalance, setSelectedCardBalance] = useState<V2Balance | null>(INITIAL_USER_SCOPED_STATE.selectedCardBalance);
  /** Which action to confirm on CardBlockScreen — set via handleOpenCardBlock
   *  before switching tab. Defaults to 'block' (backwards-compat with legacy
   *  entry-points that navigated to 'cardBlock' directly without a mode). */
  const [cardBlockMode, setCardBlockMode] = useState<CardBlockMode>(INITIAL_USER_SCOPED_STATE.cardBlockMode);
  const [cardDetail, setCardDetail] = useState<V2CardDetailResponse | null>(INITIAL_USER_SCOPED_STATE.cardDetail);
  const [cardDetailLoading, setCardDetailLoading] = useState(INITIAL_USER_SCOPED_STATE.cardDetailLoading);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(INITIAL_USER_SCOPED_STATE.selectedTransaction);
  const [selectedHistoryRecord, setSelectedHistoryRecord] = useState<V2HistoryRecord | null>(INITIAL_USER_SCOPED_STATE.selectedHistoryRecord);
  const [selectedRequest, setSelectedRequest] = useState<V2Request | null>(INITIAL_USER_SCOPED_STATE.selectedRequest);
  const [selectedMarketToken, setSelectedMarketToken] = useState<V2Token | null>(INITIAL_USER_SCOPED_STATE.selectedMarketToken);
  /** xStock open on TokenDetailScreen; takes precedence over selectedMarketToken. */
  const [selectedMarketStock, setSelectedMarketStock] = useState<V2XStock | null>(INITIAL_USER_SCOPED_STATE.selectedMarketStock);
  /** Stock pre-filled into Operations → Exchange by the Buy/Sell buttons. */
  const [exchangePreset, setExchangePreset] = useState<ExchangePreset | null>(INITIAL_USER_SCOPED_STATE.exchangePreset);
  const [tokenDetailOrigin, setTokenDetailOrigin] = useState<TabScreen>('dashboard');
  const [repeatRequest, setRepeatRequest] = useState<V2Request | null>(INITIAL_USER_SCOPED_STATE.repeatRequest);
  const [historyDetailOrigin, setHistoryDetailOrigin] = useState<TabScreen>('history');
  // An order can be opened from the Orders list or from the operation it
  // produced; back goes where it came from.
  const [orderDetailOrigin, setOrderDetailOrigin] = useState<TabScreen>('orders');
  const [balanceDetailOrigin, setBalanceDetailOrigin] = useState<TabScreen>('dashboard');
  // Origin tab for DepositAccountScreen's back button. По умолчанию
  // 'dashboard' (тап с Dashboard.deposit-section), но когда юзер пришёл
  // с All assets → Deposits tab, ставим 'allAssets' чтобы back вернул
  // его именно туда, а не на Dashboard.
  const [depositAccountOrigin, setDepositAccountOrigin] = useState<TabScreen>('dashboard');
  const [profileOrigin, setProfileOrigin] = useState<TabScreen>('dashboard');
  const [transactionOrigin, setTransactionOrigin] = useState<TabScreen>('cardHistory');
  const [reportsOrigin, setReportsOrigin] = useState<TabScreen>('dashboard');
  // Persisted network selection for WithdrawScreen (survives QR scanner navigation)
  const [withdrawNetworkId, setWithdrawNetworkId] = useState<string | null>(INITIAL_USER_SCOPED_STATE.withdrawNetworkId);
  // Operations screen — tab to focus on open + screen we came from (for back nav).
  const [operationsInitialTab, setOperationsInitialTab] = useState<OperationsTab>(INITIAL_USER_SCOPED_STATE.operationsInitialTab);
  const [operationsOrigin, setOperationsOrigin] = useState<TabScreen>('dashboard');

  // App banner state
  const [bannerData, setBannerData] = useState<V2AppBannerResponse | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const {
    isAuthenticated,
    isLoading: authLoading,
    logout,
    user,
    biometryType,
    biometricsEnabled,
    enableBiometrics,
  } = useAuth();
  const {
    isPINSetup,
    isPINSkipped,
    isPINSetupCompleted,
    isLocked,
    isLoading: pinLoading,
    setupPIN,
    skipPINSetup,
    clearPIN,
    lockApp,
  } = usePIN();
  useIdleLogout(isAuthenticated, logout);
  const canRecommendPasswordChange =
    user?.capabilities?.can_change_password !== false && !user?.is_impersonating;
  const skipBiometricRecommendation = shouldSkipBiometricRecommendation({
    isPINSetup,
    biometryType,
    biometricsEnabled,
  });

  // The app no longer locks itself. It used to ask for the PIN again three
  // seconds after going to the background and after five minutes without a
  // touch, which meant glancing at an SMS cost a PIN entry. The PIN is still
  // required on a cold start and after signing in again — PINContext locks
  // on load whenever a PIN exists.

  // Re-lock the app whenever the user becomes unauthenticated (logout, session expired, etc.)
  // so the next login always requires PIN entry again.
  useEffect(() => {
    if (!isAuthenticated && isPINSetup) {
      lockApp();
    }
  }, [isAuthenticated, isPINSetup, lockApp]);

  // --- Android hardware back button ---
  const currentScreenRef = useRef(currentScreen);
  const currentTabRef = useRef(currentTab);
  const pdfDocumentRef = useRef(pdfDocument);
  const lastBackPressRef = useRef<number>(0);

  useEffect(() => { currentScreenRef.current = currentScreen; }, [currentScreen]);
  useEffect(() => { currentTabRef.current = currentTab; }, [currentTab]);
  useEffect(() => { pdfDocumentRef.current = pdfDocument; }, [pdfDocument]);

  useEffect(() => {
    if (Platform.OS !== 'android') return;

    const onBackPress = () => {
      const tab = currentTabRef.current;

      // On dashboard — double press to exit
      if (currentScreenRef.current === 'main' && tab === 'dashboard') {
        const now = Date.now();
        if (now - lastBackPressRef.current < 2000) {
          BackHandler.exitApp();
          return true;
        }
        lastBackPressRef.current = now;
        ToastAndroid.show('Press back again to exit', ToastAndroid.SHORT);
        return true;
      }

      // On login/loading — let the system handle (exit)
      if (currentScreenRef.current !== 'main' &&
          currentScreenRef.current !== 'forgotPassword' &&
          currentScreenRef.current !== 'signup') {
        return false;
      }

      // Delegate to the shared goBack logic
      goBackRef.current();
      return true;
    };

    const subscription = BackHandler.addEventListener('hardwareBackPress', onBackPress);
    return () => subscription.remove();
  }, []); // Register once — refs always hold the latest values

  // Handle PDF preview navigation
  const handleOpenPdfPreview = useCallback((document: V2Document) => {
    setPdfDocument(document);
  }, []);

  const handleClosePdfPreview = useCallback(() => {
    setPdfDocument(null);
  }, []);

  // Handle local PDF preview (product docs)
  // Handle balance detail navigation
  const handleOpenBalanceDetail = useCallback((balance: V2Balance) => {
    setBalanceDetailOrigin(currentTab);
    setSelectedBalance(balance);
    setCurrentTab('balanceDetail');
  }, [currentTab]);

  const handleCloseBalanceDetail = useCallback(() => {
    setSelectedBalance(null);
    setCurrentTab(balanceDetailOrigin);
  }, [balanceDetailOrigin]);

  /** Open the dedicated DepositAccountScreen for an investment-balance
   *  card on the dashboard. Keyed on the investment account id — works
   *  uniformly for new deposits (snapshot fields populated in migration
   *  00080) and legacy investment accounts (snapshot stays NULL,
   *  backend deposit-view falls back to balance.change/dates). */
  const handleOpenDepositAccount = useCallback((accountId: string) => {
    setSelectedInvestmentAccountId(accountId);
    setCurrentTab('depositAccount');
  }, []);

  const handleCloseDepositAccount = useCallback(() => {
    setSelectedInvestmentAccountId(null);
    // Возвращаемся туда откуда пришли (dashboard если с главной,
    // allAssets если с экрана "All assets"). См. depositAccountOrigin.
    setCurrentTab(depositAccountOrigin);
  }, [depositAccountOrigin]);

  /** "Open new" CTA on DepositAccountScreen — routes to OpenDepositScreen
   *  and stamps the origin so its back button returns here, not to
   *  Products (the legacy PDF-preview entry point). */
  const handleOpenNewDepositFromAccount = useCallback(() => {
    setOpenDepositOrigin('depositAccount');
    setCurrentTab('openDeposit');
  }, []);

  /** "Inflow" CTA on DepositAccountScreen — routes to TopUpDepositScreen
   *  for the currently-open deposit. Re-uses selectedDepositId so back
   *  navigation lands on the same DepositAccountScreen instance. */
  const handleTopUpDeposit = useCallback(() => {
    setCurrentTab('topupDeposit');
  }, []);

  const handleCloseTopUpDeposit = useCallback(() => {
    setCurrentTab('depositAccount');
  }, []);

  /** "Close" CTA on DepositAccountScreen — routes to CloseDepositScreen
   *  for the currently-open investment account. Reuses
   *  selectedInvestmentAccountId so the back path lands on the same
   *  DepositAccountScreen instance. */
  const handleCloseDeposit = useCallback(() => {
    setCurrentTab('closeDeposit');
  }, []);

  const handleCloseCloseDeposit = useCallback(() => {
    setCurrentTab('depositAccount');
  }, []);

  // После осознанной submit'а на закрытие депозита юзеру не нужно
  // возвращаться на DepositAccountScreen — там может быть 0-balance
  // (если backend / админ моментально consume'или баланс) или просто
  // лишний экран. Уводим сразу на dashboard, зачищая selected-id чтобы
  // следующий tap по другому депозиту обновил context.
  const handleCloseSuccessRedirectDashboard = useCallback(() => {
    setSelectedInvestmentAccountId(null);
    setCurrentTab('dashboard');
  }, []);

  /** "Withdraw" CTA on DepositAccountScreen — routes to
   *  OutflowDepositScreen for the currently-open investment account. */
  const handleOutflowDeposit = useCallback(() => {
    setCurrentTab('outflowDeposit');
  }, []);

  const handleCloseOutflowDeposit = useCallback(() => {
    setCurrentTab('depositAccount');
  }, []);

  /** Link "close the deposit" inside the partial-withdraw warning bar
   *  on OutflowDepositScreen — switches to CloseDepositScreen for the
   *  same accountId so the user can close instead of partial withdraw. */
  const handleCloseFromOutflow = useCallback(() => {
    setCurrentTab('closeDeposit');
  }, []);

  // Handle card detail screen navigation. One load at a time: opening
  // another card, or leaving for Top-up, drops a load still out — and its
  // spinner (utils/cardDetailLoader).
  const cardDetailLoader = useMemo(
    () => createCardDetailLoader({ getCard: v2GetCard, setDetail: setCardDetail, setLoading: setCardDetailLoading }),
    [],
  );
  const handleOpenCardDetail = useCallback(async (balance: V2Balance) => {
    setSelectedCardBalance(balance);
    setCurrentTab('cardDetail');
    await cardDetailLoader.load(balance.wallet_address ?? '');
  }, [cardDetailLoader]);

  const handleCloseCardDetail = useCallback(() => {
    // A detail still loading for the card being closed must not land later.
    cardDetailLoader.cancel();
    setSelectedCardBalance(null);
    setCardDetail(null);
    setCurrentTab('dashboard');
  }, [cardDetailLoader]);

  // Card block/unblock — оба flow'а идут на CardBlockScreen с prop'ом mode.
  // Legacy: раньше unblock отправлял в support chat prefill'ом; теперь
  // прямой API call (v2UnblockCard) через тот же экран с mode='unblock'.
  const handleOpenCardBlock = useCallback((mode: CardBlockMode) => {
    setCardBlockMode(mode);
    setCurrentTab('cardBlock');
  }, []);

  // Open Operations screen with the given tab pre-selected.
  // Stamps the tab the user came from so the back-button knows where to go.
  const openOperations = useCallback((tab: OperationsTab) => {
    setRepeatRequest(null);
    setExchangePreset(null);
    setOperationsInitialTab(tab);
    setOperationsOrigin(currentTab);
    setCurrentTab('operations');
  }, [currentTab]);

  // BalanceDetail "Inflow" button → Operations on Inflow tab.
  const handleOpenDeposit = useCallback(() => {
    if (selectedBalance) {
      openOperations('inflow');
    }
  }, [selectedBalance, openOperations]);

  // BalanceDetail "Withdraw" button → Operations on Withdraw tab.
  const handleOpenWithdraw = useCallback(() => {
    if (selectedBalance) {
      openOperations('withdraw');
    }
  }, [selectedBalance, openOperations]);

  const openTokenDetail = useCallback((target: { token?: V2Token; stock?: V2XStock }, origin: TabScreen) => {
    setSelectedMarketToken(target.token ?? null);
    setSelectedMarketStock(target.stock ?? null);
    setTokenDetailOrigin(origin);
    setCurrentTab('tokenDetail');
  }, []);

  // Token / stock screen Buy/Sell → Operations on the Exchange tab with the
  // token filled in. Operations needs a current-account balance for its
  // other tabs; outside a balance flow none is selected yet, so the user's
  // USDT current account is used (any current account as a fallback).
  //   stock  — one token row per stock: Buy by id; Sell by id for USDT.
  //   crypto — several rows per ticker (networks, precisions), the holding
  //            may sit on any: picked by symbol; Sell for USDT.
  const openExchange = useCallback(async (preset: ExchangePreset | null) => {
    let balance = selectedBalance;
    if (!balance) {
      try {
        const res = await v2GetMyBalances();
        const current = (res.balances ?? []).filter(isCurrentAccountBalance);
        balance = current.find(b => b.token_symbol === 'USDT' && b.provider_name === 'virtual')
          ?? current.find(b => b.token_symbol === 'USDT')
          ?? current[0]
          ?? null;
      } catch {
        balance = null;
      }
    }
    if (!balance) {
      Alert.alert('Exchange unavailable', 'You need a current account to place an order.');
      return;
    }
    setSelectedBalance(balance);
    openOperations('exchange');
    // Tokens only: the limit price is always typed by the client.
    setExchangePreset(preset);
  }, [openOperations, selectedBalance]);

  const handleOpenMarketExchange = useCallback(async (
    target: { stock: V2XStock } | { token: V2Token },
    side: 'buy' | 'sell',
  ) => {
    let preset: ExchangePreset;
    if ('stock' in target) {
      const tokenId = target.stock.token_id;
      if (!tokenId) {
        return;
      }
      preset = side === 'buy'
        ? { id: Date.now(), buyTokenId: tokenId }
        : { id: Date.now(), sellTokenId: tokenId, buySymbol: 'USDT' };
    } else {
      const symbol = target.token.symbol;
      preset = side === 'buy'
        ? { id: Date.now(), buySymbol: symbol }
        : { id: Date.now(), sellSymbol: symbol, buySymbol: symbol.toUpperCase() === 'USDT' ? undefined : 'USDT' };
    }
    await openExchange(preset);
  }, [openExchange]);

  // Dashboard Exchange button — no account is in hand, so the form picks
  // the first sell row itself, which the ordering makes USDT-TRC20.
  const handleDashboardExchange = useCallback(() => {
    void openExchange(null);
  }, [openExchange]);

  // Exchange from an account screen — open on the account being looked at.
  // The form never sees selectedBalance (Operations passes it to Inflow and
  // Withdraw only), so the account travels as a preset.
  const handleBalanceExchange = useCallback(() => {
    void openExchange(presetForBalance(selectedBalance, Date.now()));
  }, [openExchange, selectedBalance]);

  const handleRepeatRequest = useCallback(async (request: V2Request) => {
    if (!['withdraw', 'card_topup', 'current_to_deposit', 'deposit_to_current'].includes(request.type_code)) {
      return;
    }

    try {
      const details = await v2GetMyRequest(request.id);
      setRepeatRequest(details);
      setOperationsOrigin('orderDetail');

      if (details.type_code === 'withdraw' || details.type_code === 'card_topup') {
        const balances = (await v2GetMyBalances()).balances ?? [];
        const source = balances.find((balance) => balance.id === details.balance_id);
        if (!source) {
          throw new Error('The original source account is no longer available.');
        }
        setSelectedBalance(source);

        if (details.type_code === 'card_topup') {
          const card = balances.find((balance) => balance.id === details.destination_balance_id) ?? null;
          if (!card) {
            throw new Error('The original card is no longer available.');
          }
          setSelectedCardBalance(card);
          setOperationsInitialTab('topup');
        } else {
          setOperationsInitialTab('withdraw');
        }
        setCurrentTab('operations');
        return;
      }

      const accountId = details.investment_account_id ?? details.account_id;
      if (!accountId) {
        throw new Error('The original deposit account is no longer available.');
      }
      setSelectedInvestmentAccountId(accountId);
      setCurrentTab(details.type_code === 'current_to_deposit' ? 'topupDeposit' : 'outflowDeposit');
    } catch (error) {
      setRepeatRequest(null);
      Alert.alert('Unable to repeat operation', error instanceof Error ? error.message : 'Please try again later.');
    }
  }, []);

  // The operation card links back to the order that produced it.
  const handleOpenRequestFromHistory = useCallback(async (requestID: string) => {
    try {
      const request = await v2GetMyRequest(requestID);
      setSelectedRequest(request);
      setOrderDetailOrigin('historyDetail');
      setCurrentTab('orderDetail');
    } catch (error) {
      Alert.alert(
        'Request unavailable',
        error instanceof Error ? error.message : 'Please try again later.',
      );
    }
  }, []);

  const handleOpenRequestReceipt = useCallback(async (request: V2Request) => {
    try {
      const details = await v2GetMyRequest(request.id);
      if (!details.transaction_ids?.length) {
        Alert.alert('Receipt unavailable', 'No completed transaction is linked to this order.');
        return;
      }
      const history = await v2GetMyHistory();
      const record = history.records.find((item) => details.transaction_ids?.includes(item.id));
      if (!record) {
        Alert.alert('Receipt unavailable', 'The linked transaction is no longer available.');
        return;
      }
      setSelectedHistoryRecord(record);
      setHistoryDetailOrigin('orderDetail');
      setCurrentTab('historyDetail');
    } catch (error) {
      Alert.alert('Unable to open receipt', error instanceof Error ? error.message : 'Please try again later.');
    }
  }, []);

  const handleCloseRepeatedDeposit = useCallback(() => {
    setRepeatRequest(null);
    setCurrentTab('orderDetail');
  }, []);

  const handleRequestSupport = useCallback((request: V2Request) => {
    setSupportRequestReference({ id: request.id, number: request.number });
    setSupportInitialRoute('personal');
    setCurrentTab('support');
  }, []);

  // Dashboard investment-card tile → DepositAccountScreen (account-based).
  // Routes by `balance.account_id` — works for both new deposits and
  // legacy investment accounts (the new deposit-view endpoint sources
  // term/apy from v1_2_accounts snapshot fields when present, with
  // fallback to balance.change/dates for pre-migration-00080 rows).
  // No more "balance.deposit_id required" gate — the deposit row is no
  // longer the read source.
  const handleOpenInvestmentDeposit = useCallback((balance: V2Balance) => {
    // Defensive — log when the tile is tappable on the dashboard but the
    // expected account_id link is missing. Older legacy balances or
    // mid-flight backend snapshots may leave it null; surface that here
    // instead of silently swallowing the tap so the user (or QA) gets
    // a visible signal in the console.
    if (!balance.account_id) {
      console.warn('[Dashboard] Deposit tile tapped but balance.account_id is missing:', {
        balance_id: balance.id,
        token_symbol: balance.token_symbol,
        account_type_code: balance.account_type_code,
        deposit_id: balance.deposit_id,
      });
      return;
    }
    // Запоминаем откуда пришли через currentTabRef (current tab via
    // ref'а — не зависит от useCallback dep array, всегда последнее
    // значение). Был bug когда handler перерождался ассинхронно после
    // setCurrentTab + stale-closure захватывал старое currentTab,
    // приводящее к тому что DepositAccount открывался не тем
    // экраном — теперь handler без deps.
    //
    // Splitting по account_type_code: 'deposit' → DepositAccountScreen
    // (классический term-deposit с APY/term/action row), 'investment' →
    // InvestmentAccountScreen (T-Fund-like: hero + history, никаких
    // action buttons). Оба типа приходят через один onInvestmentPress
    // handler на дашборде, но роутинг ветвится здесь.
    if (balance.account_type_code === 'investment') {
      setInvestmentAccountOrigin(currentTabRef.current);
      setSelectedTFundAccountId(balance.account_id);
      setCurrentTab('investmentAccount');
      return;
    }
    setDepositAccountOrigin(currentTabRef.current);
    setSelectedInvestmentAccountId(balance.account_id);
    setCurrentTab('depositAccount');
  }, []);

  // Top-up — opens Operations on Top-up tab for the first card-type balance.
  // No card → show NoCardScreen so the user can apply.
  // Card-apply CTA — shared by the standalone NoCardScreen route and the
  // inline NoCardScreen rendered inside the Operations Top-up tab. Drops
  // Called after NoCardScreen has successfully POSTed the card application
  // (PIN flow handled internally). Closes the apply screen and returns the
  // user to the dashboard.
  const handleCardApplicationSubmitted = useCallback(() => {
    setCurrentTab('dashboard');
  }, []);

  // `fromCard` is the card whose screen "Top up" was pressed on: Top-up opens
  // on it, and its details stay for the way back. Elsewhere it is absent and
  // the first card is taken.
  const handleOpenTopUp = useCallback(async (fromCard?: V2Balance | null) => {
    try {
      const res = await v2GetMyBalances();
      const all = res.balances ?? [];
      // No card → still route to Operations on the Top-up tab; that tab
      // detects a null card balance and renders NoCardScreen inline (with
      // the "Apply card" CTA). Inflow/Withdraw tabs remain accessible.
      const card = pickTopUpCard(all, fromCard);
      if (!fromCard || card !== fromCard) {
        setSelectedCardBalance(card);
        // A detail still loading for the card left behind is dropped, spinner
        // and all — the card screen is opened afresh after Top-up.
        cardDetailLoader.cancel();
        setCardDetail(null);
      }
      // Provide a current-account fallback for the Inflow/Withdraw tabs in
      // Operations — never a deposit or xStocks account.
      const fallback = all.find(isCurrentAccountBalance) ?? all[0];
      if (fallback) {
        setSelectedBalance(fallback);
      }
      openOperations('topup');
    } catch (err) {
      console.warn('[App] Top-up: failed to load balances', err);
    }
  }, [openOperations, cardDetailLoader]);

  // Dashboard-level Inflow / Withdraw — route through Operations. Inflow
  // arrives with no token in hand, so it takes the current account holding
  // the most; the Inflow screen then picks the network within that token.
  // Whichever way a client reaches Inflow, they land where their money is.
  // Withdraw keeps the first current balance it finds.
  const handleDashboardInflow = useCallback(async () => {
    try {
      const res = await v2GetMyBalances();
      const current = (res.balances ?? []).filter(isCurrentAccountBalance);
      const amountOf = (b: V2Balance) =>
        parseFloat(b.available_amount ?? b.amount ?? '0') || 0;
      const balance = [...current].sort((a, b) => amountOf(b) - amountOf(a))[0]
        ?? (res.balances ?? [])[0];
      if (!balance) {
        console.warn('[App] Dashboard Inflow: no balance available');
        return;
      }
      setSelectedBalance(balance);
      openOperations('inflow');
    } catch (err) {
      console.warn('[App] Dashboard Inflow: failed to load balances', err);
    }
  }, [openOperations]);

  const handleDashboardWithdraw = useCallback(async () => {
    try {
      const res = await v2GetMyBalances();
      const balance = (res.balances ?? []).find(isCurrentAccountBalance) ?? (res.balances ?? [])[0];
      if (!balance) {
        console.warn('[App] Dashboard Withdraw: no balance available');
        return;
      }
      setSelectedBalance(balance);
      openOperations('withdraw');
    } catch (err) {
      console.warn('[App] Dashboard Withdraw: failed to load balances', err);
    }
  }, [openOperations]);

  // Handle transfer success screen
  const handleTransferSuccess = useCallback(() => {
    setCurrentTab('transferSuccess');
  }, []);

  const handleBackToDashboard = useCallback(() => {
    setSelectedBalance(null);
    setCurrentTab('dashboard');
  }, []);

  // Handle QR scanner
  const [scannedAddress, setScannedAddress] = useState<string | null>(INITIAL_USER_SCOPED_STATE.scannedAddress);

  const handleOpenQRScanner = useCallback(() => {
    setCurrentTab('qrScanner');
  }, []);

  const handleQRScan = useCallback((address: string) => {
    setScannedAddress(address);
    setOperationsInitialTab('withdraw');
    setCurrentTab('operations');
  }, []);

  const handleCloseQRScanner = useCallback(() => {
    setOperationsInitialTab('withdraw');
    setCurrentTab('operations');
  }, []);

  // ── Centralised "go back" — used by Android back handler AND swipe-back gesture ──
  const goBack = useCallback(() => {
    if (pdfDocument) {
      setPdfDocument(null);
      return;
    }
    if (currentScreen === 'forgotPassword') {
      setCurrentScreen('login');
      return;
    }
    if (currentScreen === 'signup') {
      setCurrentScreen('login');
      return;
    }
    if (currentScreen !== 'main') return;

    const target = backTargetFor(currentTab, {
      balanceDetail: balanceDetailOrigin,
      depositAccount: depositAccountOrigin,
      investmentAccount: investmentAccountOrigin,
      openDeposit: openDepositOrigin,
      transaction: transactionOrigin,
      operations: operationsOrigin,
      tokenDetail: tokenDetailOrigin,
      profile: profileOrigin,
      reports: reportsOrigin,
      historyDetail: historyDetailOrigin,
      orderDetail: orderDetailOrigin,
    }, { repeatingOrder: repeatRequest != null });
    if (target == null) {
      return; // 'dashboard' — root screen, nothing to go back to
    }
    // A details screen whose record is gone goes to its list instead.
    const landing = backLandingFor(target, {
      historyRecord: selectedHistoryRecord != null,
      request: selectedRequest != null,
    });
    // What the screen being left had picked goes with it.
    switch (currentTab) {
      case 'balanceDetail':
      case 'transferSuccess':
        setSelectedBalance(null);
        break;
      case 'depositAccount':
        setSelectedInvestmentAccountId(null);
        break;
      case 'cardDetail':
        cardDetailLoader.cancel();
        setSelectedCardBalance(null);
        setCardDetail(null);
        setCardDetailLoading(false);
        break;
      case 'transactionDetail':
        setSelectedTransaction(null);
        break;
      case 'operations':
        setWithdrawNetworkId(null);
        setRepeatRequest(null);
        setExchangePreset(null);
        break;
      case 'topupDeposit':
      case 'outflowDeposit':
        // A repeat goes back to its order; the next top-up is not a repeat.
        setRepeatRequest(null);
        break;
      case 'qrScanner':
        setOperationsInitialTab('withdraw');
        break;
      case 'historyDetail':
        setSelectedHistoryRecord(null);
        break;
      case 'orderDetail':
        setSelectedRequest(null);
        break;
    }
    setCurrentTab(landing as TabScreen);
  }, [
    pdfDocument, currentScreen, currentTab,
    transactionOrigin, operationsOrigin, profileOrigin,
    reportsOrigin, historyDetailOrigin, orderDetailOrigin, balanceDetailOrigin,
    depositAccountOrigin, tokenDetailOrigin, investmentAccountOrigin, openDepositOrigin,
    selectedHistoryRecord, selectedRequest, repeatRequest, cardDetailLoader,
  ]);

  // Ref so that PanResponder always sees the latest goBack without re-creating the responder
  const goBackRef = useRef(goBack);
  useEffect(() => { goBackRef.current = goBack; }, [goBack]);

  // Track where the touch started (for left-edge detection)
  const touchStartX = useRef(0);

  // Swipe-back gesture: swipe right starting from the left edge (x < 30)
  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: (evt) => {
        touchStartX.current = evt.nativeEvent.pageX;
        return false; // don't capture yet — let children handle taps normally
      },
      onMoveShouldSetPanResponder: (_evt, gestureState) =>
        touchStartX.current < 30 &&
        gestureState.dx > 10 &&
        Math.abs(gestureState.dy) < Math.abs(gestureState.dx) * 1.5,
      onPanResponderRelease: (_evt, gestureState) => {
        if (
          touchStartX.current < 30 &&
          gestureState.dx > 60 &&
          Math.abs(gestureState.dy) < 120
        ) {
          goBackRef.current();
        }
      },
    }),
  ).current;

  // Handle tab navigation
  const handleTabNavigate = useCallback((screen: string) => {
    if (screen === 'support') {
      setSupportInitialRoute('chooser');
    }
    if (screen === 'profile') {
      setProfileOrigin(currentTab);
    }
    if (screen === 'reports') {
      setReportsOrigin(currentTab);
    }
    if (screen === 'operations') {
      setOperationsOrigin(currentTab);
    }
    // Dead-route guard: routes that have been retired (e.g. 'depositCard')
    // would silently fall through to the default DashboardV2Screen branch,
    // breaking subsequent navigation. Surface them in dev so the next drift
    // shows up immediately instead of presenting as "nav stopped working".
    const knownRoutes: ReadonlySet<string> = new Set([
      'dashboard', 'orders', 'orderDetail', 'products', 'history', 'historyDetail', 'reports', 'support',
      'profile', 'changePassword', 'changePIN', 'pinSetup', 'userSessions',
      'balanceDetail', 'cardDetail', 'cardHistory', 'cardInfo', 'cardBlock',
      'operations', 'transactionDetail', 'transferSuccess', 'allAssets',
      'qrScanner', 'totalBalance', 'popularTokens', 'tokenDetail', 'notifications', 'noCard',
    ]);
    if (!knownRoutes.has(screen)) {
      console.warn(`[App] handleTabNavigate: unknown screen "${screen}" — routing fallthrough will render dashboard`);
    }
    setCurrentTab(screen as TabScreen);
  }, [currentTab]);

  // Подключить навигацию для пуш-уведомлений
  useEffect(() => {
    notificationService.setNavigationHandler((screen: string) => {
      setCurrentTab(screen as TabScreen);
    });
  }, []);

  // Global JS error trap — without this, an uncaught error during render or
  // an async setState swallows silently and the next interaction looks like
  // "navigation broke". We chain onto the existing handler so RN's red-box
  // still appears in dev.
  useEffect(() => {
    const G: any = global;
    const prev = G?.ErrorUtils?.getGlobalHandler?.();
    G?.ErrorUtils?.setGlobalHandler?.((err: Error, isFatal?: boolean) => {
      console.warn('[App] Uncaught JS error', { isFatal, message: err?.message, stack: err?.stack });
      if (prev) prev(err, isFatal);
    });
    return () => {
      if (prev) G?.ErrorUtils?.setGlobalHandler?.(prev);
    };
  }, []);

  // Log app open on mount and check if returning user
  useEffect(() => {
    analyticsService.logAppOpen();

    // Check if user has logged in before (decides the first-login security steps)
    const checkFirstLogin = async () => {
      const hasLogged = await hasLoggedInBefore();
      const securityStep = await initializeFirstLoginSecurityStep(hasLogged);
      setFirstLoginSecurityStepState(securityStep);
      setCheckedFirstLogin(true);
    };
    checkFirstLogin();

    // Fetch app banner from backend
    const fetchBanner = async () => {
      try {
        const platform = Platform.OS as 'ios' | 'android';
        const appVersion = DeviceInfo.getVersion();
        const data = await v2GetAppBanner(platform, appVersion);
        if (data && data.display_mode !== 'not_show') {
          // For 'show_and_block': only show if current app version < min_required_version
          if (data.display_mode === 'show_and_block' && data.min_required_version) {
            if (compareVersions(appVersion, data.min_required_version) >= 0) {
              return; // current version is up to date, don't show blocking banner
            }
          }
          setBannerData(data);
        }
      } catch {
        // Silently ignore — do not show banner on network error
      }
    };
    fetchBanner();
  }, []);

  // A user's balance, card, requests and scanned address must not outlive
  // them: on sign-out, and when another user takes over without one (another
  // login, the start or end of "Login as user"), everything they picked goes,
  // with any support message prepared for them, and the next user starts on
  // the dashboard. The record names a setter for every user-scoped field; one
  // left out does not compile.
  const handleUserGone = useCallback(() => {
    resetUserScopedState({
      pdfDocument: setPdfDocument,
      selectedBalance: setSelectedBalance,
      selectedInvestmentAccountId: setSelectedInvestmentAccountId,
      selectedTFundAccountId: setSelectedTFundAccountId,
      selectedCardBalance: setSelectedCardBalance,
      cardBlockMode: setCardBlockMode,
      cardDetail: setCardDetail,
      cardDetailLoading: setCardDetailLoading,
      selectedTransaction: setSelectedTransaction,
      selectedHistoryRecord: setSelectedHistoryRecord,
      selectedRequest: setSelectedRequest,
      selectedMarketToken: setSelectedMarketToken,
      selectedMarketStock: setSelectedMarketStock,
      exchangePreset: setExchangePreset,
      repeatRequest: setRepeatRequest,
      withdrawNetworkId: setWithdrawNetworkId,
      operationsInitialTab: setOperationsInitialTab,
      scannedAddress: setScannedAddress,
    });
    consumeSupportPrefill();
    setCurrentTab('dashboard');
  }, []);
  useOnUserGone(isAuthenticated ? user?.id ?? null : null, handleUserGone);

  // Determine screen based on auth state
  useEffect(() => {
    if (!checkedFirstLogin) {
      setCurrentScreen('loading');
      return;
    }

    if (authLoading) {
      // Still checking auth state, stay where we are
      return;
    }

    setCurrentScreen(isAuthenticated ? 'main' : 'login');
  }, [checkedFirstLogin, authLoading, isAuthenticated]);

  // Log screen views
  useEffect(() => {
    if (currentScreen !== 'loading') {
      analyticsService.logScreenView(currentScreen);
    }
  }, [currentScreen]);

  // Navigate to Sign Up from Login
  const handleGoToSignUp = () => {
    setCurrentScreen('signup');
  };

  // Navigate to Login from Sign Up
  const handleGoToLogin = () => {
    setCurrentScreen('login');
  };

  // Sign Up success → user will be authenticated, useEffect will navigate
  const handleSignUpSuccess = () => {
    // For now, just go to login since we don't have registration integrated yet
    setCurrentScreen('login');
  };

  // Forgot password handler → go to Forgot Password screen
  const handleForgotPassword = () => {
    setCurrentScreen('forgotPassword');
  };

  // Back from Forgot Password → go to Login
  const handleBackFromForgotPassword = () => {
    setCurrentScreen('login');
  };

  // Handle password change - logout user since all tokens are revoked
  const handlePasswordChanged = useCallback(async () => {
    await clearPIN(); // Clear PIN when password changes
    await logout();
    // The auth state change will redirect to login
  }, [logout, clearPIN]);

  // Handle PIN setup completion
  const handlePINSetupComplete = useCallback(async (pin: string) => {
    // setupPIN answers false when the Keychain refused the PIN; the setup
    // screen only hears of a failure that is thrown, and said "saved" anyway.
    const saved = await setupPIN(pin);
    if (!saved) {
      throw new Error('Failed to save PIN');
    }
  }, [setupPIN]);

  // Handle PIN setup skip
  const handlePINSetupSkip = useCallback(async () => {
    await skipPINSetup();
  }, [skipPINSetup]);

  const moveFirstLoginSecurityFlow = useCallback(
    async (step: FirstLoginSecurityStep) => {
      // Update the UI immediately. If Keychain persistence fails, the current
      // session remains usable and the recommendation may safely reappear on
      // the next launch instead of blocking the user.
      setFirstLoginSecurityStepState(step);
      await setFirstLoginSecurityStep(step);
    },
    [],
  );

  const handleEnableFirstLoginBiometrics = useCallback(async (): Promise<boolean> => {
    const enabled = await enableBiometrics();
    if (enabled) {
      await moveFirstLoginSecurityFlow('password');
    }
    return enabled;
  }, [enableBiometrics, moveFirstLoginSecurityFlow]);

  const handleFirstLoginBiometricsLater = useCallback(async () => {
    await moveFirstLoginSecurityFlow('password');
  }, [moveFirstLoginSecurityFlow]);

  const handleFirstLoginPasswordLater = useCallback(async () => {
    setCurrentTab('dashboard');
    await moveFirstLoginSecurityFlow('completed');
  }, [moveFirstLoginSecurityFlow]);

  const handleOpenSettingsFromFirstLogin = useCallback(async () => {
    setProfileOrigin('dashboard');
    setCurrentTab('profile');
    await moveFirstLoginSecurityFlow('completed');
  }, [moveFirstLoginSecurityFlow]);

  // Preserve the existing rule that biometrics require an enabled PIN. The
  // recommendation is also unnecessary when the device has no supported
  // sensor or biometric login was already configured outside this flow.
  useEffect(() => {
    if (
      isAuthenticated &&
      currentScreen === 'main' &&
      isPINSetupCompleted &&
      firstLoginSecurityStep === 'biometrics' &&
      skipBiometricRecommendation
    ) {
      moveFirstLoginSecurityFlow('password').catch(error => {
        console.error('Failed to skip unavailable biometrics recommendation:', error);
      });
    }
  }, [
    biometryType,
    biometricsEnabled,
    currentScreen,
    firstLoginSecurityStep,
    isAuthenticated,
    isPINSetupCompleted,
    moveFirstLoginSecurityFlow,
    skipBiometricRecommendation,
  ]);

  // Server capabilities are authoritative. If changing a password is not
  // available for this session, do not send the user to a settings row that
  // is intentionally hidden.
  useEffect(() => {
    if (
      isAuthenticated &&
      currentScreen === 'main' &&
      firstLoginSecurityStep === 'password' &&
      !canRecommendPasswordChange
    ) {
      moveFirstLoginSecurityFlow('completed').catch(error => {
        console.error('Failed to complete unavailable password recommendation:', error);
      });
    }
  }, [
    canRecommendPasswordChange,
    currentScreen,
    firstLoginSecurityStep,
    isAuthenticated,
    moveFirstLoginSecurityFlow,
  ]);

  // Navigate to change password screen
  const handleGoToChangePassword = useCallback(() => {
    setCurrentTab('changePassword');
  }, []);

  // Navigate to change PIN screen
  const handleGoToChangePIN = useCallback(() => {
    setCurrentTab('changePIN');
  }, []);

  // Navigate to PIN setup screen from profile
  const handleGoToPINSetup = useCallback(() => {
    setCurrentTab('pinSetup');
  }, []);

  // History screen with its Transactions | Orders tabs. The 'orders' route is
  // the Orders tab (kept for "View request status", order detail Back and
  // push navigation). Both routes render the same element in the same slot,
  // so switching tabs keeps the screen mounted.
  const renderHistory = (tab: HistoryTab) => (
    <TransactionHistoryScreen
      onNavigate={handleTabNavigate}
      activeTab={tab}
      onTabChange={(next) => setCurrentTab(next === 'orders' ? 'orders' : 'history')}
      onRecordPress={(record: V2HistoryRecord) => {
        setSelectedHistoryRecord(record);
        setHistoryDetailOrigin('history');
        setCurrentTab('historyDetail');
      }}
      onRequestPress={(request) => {
        setSelectedRequest(request);
        setOrderDetailOrigin('orders');
        setCurrentTab('orderDetail');
      }}
    />
  );

  const renderScreen = () => {
    // Keep the login form mounted while a login request is in flight. The
    // screen owns its submit spinner and lockout state; replacing it with the
    // global loader would clear the form and race persisted attempt updates.
    if ((authLoading && currentScreen !== 'login') || pinLoading) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.brandOrange} />
        </View>
      );
    }

    // Show banner screen first if banner data is available and not dismissed
    if (bannerData && !bannerDismissed) {
      return (
        <BannerScreen
          banner={bannerData}
          onDismiss={() => setBannerDismissed(true)}
        />
      );
    }

    // If authenticated and locked, show PIN unlock screen
    if (isAuthenticated && isPINSetup && isLocked) {
      return <PINUnlockScreen />;
    }

    // If authenticated and PIN setup was never completed (first login ever), show PIN setup
    // isPINSetupCompleted is stored in Keychain and persists across app reinstalls
    if (isAuthenticated && !isPINSetupCompleted && currentScreen === 'main') {
      return (
        <PINSetupScreen
          onComplete={handlePINSetupComplete}
          onSkip={handlePINSetupSkip}
        />
      );
    }

    // One-time recommendations run only after the PIN decision. Conditions
    // that auto-skip a step are handled by effects above; render a white
    // transition surface for that brief state so Dashboard never flashes.
    if (isAuthenticated && isPINSetupCompleted && currentScreen === 'main') {
      if (firstLoginSecurityStep === 'biometrics') {
        if (!skipBiometricRecommendation && biometryType) {
          return (
            <BiometricRecommendationScreen
              biometryType={biometryType}
              onEnable={handleEnableFirstLoginBiometrics}
              onLater={handleFirstLoginBiometricsLater}
            />
          );
        }

        return (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={COLORS.brandOrange} />
          </View>
        );
      }

      if (firstLoginSecurityStep === 'password') {
        if (canRecommendPasswordChange) {
          return (
            <PasswordRecommendationScreen
              onOpenSettings={handleOpenSettingsFromFirstLogin}
              onLater={handleFirstLoginPasswordLater}
            />
          );
        }

        return (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={COLORS.brandOrange} />
          </View>
        );
      }
    }

    switch (currentScreen) {
      case 'login':
        return (
          <LoginScreen
            onForgotPassword={handleForgotPassword}
          />
        );
      case 'signup':
        return (
          <SignUpScreen
            onSignUp={handleSignUpSuccess}
            onLogin={handleGoToLogin}
          />
        );
      case 'forgotPassword':
        return (
          <ForgotPasswordScreen
            onBack={handleBackFromForgotPassword}
          />
        );
      case 'main':
        // Render the current tab screen
        switch (currentTab) {
          case 'dashboard':
            return (
              <DashboardV2Screen
                onNavigate={handleTabNavigate}
                onBalancePress={handleOpenBalanceDetail}
                onCardPress={handleOpenCardDetail}
                onInvestmentPress={handleOpenInvestmentDeposit}
                onInflow={handleDashboardInflow}
                onWithdraw={handleDashboardWithdraw}
                onTopUp={() => handleOpenTopUp()}
                onExchange={handleDashboardExchange}
                onTokenPress={(token) => openTokenDetail({ token }, 'dashboard')}
                onStockPress={(stock) => openTokenDetail({ stock }, 'dashboard')}
              />
            );
          case 'balanceDetail':
            if (selectedBalance) {
              return (
                <BalanceDetailScreen
                  balance={selectedBalance}
                  onBack={handleCloseBalanceDetail}
                  onNavigate={handleTabNavigate}
                  onDeposit={handleOpenDeposit}
                  onWithdraw={handleOpenWithdraw}
                  onTopUp={() => handleOpenTopUp()}
                  onExchange={handleBalanceExchange}
                  onHistoryPress={(record: V2HistoryRecord) => {
                    setSelectedHistoryRecord(record);
                    setHistoryDetailOrigin('balanceDetail');
                    setCurrentTab('historyDetail');
                  }}
                />
              );
            }
            return <DashboardV2Screen onNavigate={handleTabNavigate} onBalancePress={handleOpenBalanceDetail} onInvestmentPress={handleOpenInvestmentDeposit} onCardPress={handleOpenCardDetail} />;
          case 'depositAccount':
            if (selectedInvestmentAccountId) {
              return (
                <DepositAccountScreen
                  accountId={selectedInvestmentAccountId}
                  onBack={handleCloseDepositAccount}
                  onNavigate={handleTabNavigate}
                  onOpenNewDeposit={handleOpenNewDepositFromAccount}
                  onTopUp={handleTopUpDeposit}
                  onClose={handleCloseDeposit}
                  onWithdraw={handleOutflowDeposit}
                />
              );
            }
            return <DashboardV2Screen onNavigate={handleTabNavigate} onBalancePress={handleOpenBalanceDetail} onInvestmentPress={handleOpenInvestmentDeposit} onCardPress={handleOpenCardDetail} />;
          case 'investmentAccount':
            if (selectedTFundAccountId) {
              return (
                <InvestmentAccountScreen
                  accountId={selectedTFundAccountId}
                  onBack={() => setCurrentTab(investmentAccountOrigin)}
                  onNavigate={handleTabNavigate}
                />
              );
            }
            return <DashboardV2Screen onNavigate={handleTabNavigate} onBalancePress={handleOpenBalanceDetail} onInvestmentPress={handleOpenInvestmentDeposit} onCardPress={handleOpenCardDetail} />;
          case 'closeDeposit':
            if (selectedInvestmentAccountId) {
              return (
                <CloseDepositScreen
                  accountId={selectedInvestmentAccountId}
                  onBack={handleCloseCloseDeposit}
                  onNavigate={handleTabNavigate}
                  // success → dashboard (юзер submit'ил close — депозит
                  // больше не нужно показывать). Back-кнопка остаётся на
                  // DepositAccount (юзер передумал submit'ить, экран
                  // депозита ещё актуален).
                  onCloseSuccess={handleCloseSuccessRedirectDashboard}
                />
              );
            }
            return <DashboardV2Screen onNavigate={handleTabNavigate} onBalancePress={handleOpenBalanceDetail} onInvestmentPress={handleOpenInvestmentDeposit} onCardPress={handleOpenCardDetail} />;
          case 'topupDeposit':
            if (selectedInvestmentAccountId) {
              return (
                <TopUpDepositScreen
                  accountId={selectedInvestmentAccountId}
                  onBack={goBack}
                  onNavigate={handleTabNavigate}
                  onTopupSuccess={repeatRequest ? handleCloseRepeatedDeposit : handleCloseTopUpDeposit}
                  repeatPrefill={repeatRequest ? {
                    sourceBalanceId: repeatRequest.balance_id,
                    amount: repeatRequest.amount,
                  } : undefined}
                />
              );
            }
            return <DashboardV2Screen onNavigate={handleTabNavigate} onBalancePress={handleOpenBalanceDetail} onInvestmentPress={handleOpenInvestmentDeposit} onCardPress={handleOpenCardDetail} />;
          case 'outflowDeposit':
            if (selectedInvestmentAccountId) {
              return (
                <OutflowDepositScreen
                  accountId={selectedInvestmentAccountId}
                  onBack={goBack}
                  onNavigate={handleTabNavigate}
                  onOutflowSuccess={repeatRequest ? handleCloseRepeatedDeposit : handleCloseOutflowDeposit}
                  onCloseDeposit={handleCloseFromOutflow}
                  repeatPrefill={repeatRequest ? {
                    destinationBalanceId: repeatRequest.destination_balance_id,
                    amount: repeatRequest.amount,
                  } : undefined}
                />
              );
            }
            return <DashboardV2Screen onNavigate={handleTabNavigate} onBalancePress={handleOpenBalanceDetail} onInvestmentPress={handleOpenInvestmentDeposit} onCardPress={handleOpenCardDetail} />;
          case 'cardDetail':
            if (selectedCardBalance) {
              return (
                <CardScreen
                  balance={selectedCardBalance}
                  cardDetail={cardDetail}
                  cardDetailLoading={cardDetailLoading}
                  onBack={handleCloseCardDetail}
                  onNavigate={handleTabNavigate}
                  onTransactionPress={(tx: any) => {
                    setSelectedTransaction(tx);
                    setTransactionOrigin('cardDetail');
                    setCurrentTab('transactionDetail');
                  }}
                  onDeposit={() => handleOpenTopUp(selectedCardBalance)}
                />
              );
            }
            return <DashboardV2Screen onNavigate={handleTabNavigate} onBalancePress={handleOpenBalanceDetail} onInvestmentPress={handleOpenInvestmentDeposit} onCardPress={handleOpenCardDetail} />;
          case 'cardHistory':
            if (selectedCardBalance) {
              return (
                <CardHistoryScreen
                  balance={selectedCardBalance}
                  onBack={() => setCurrentTab('cardDetail')}
                  onNavigate={handleTabNavigate}
                  onTransactionPress={(tx: any) => {
                    setSelectedTransaction(tx);
                    setTransactionOrigin('cardHistory');
                    setCurrentTab('transactionDetail');
                  }}
                />
              );
            }
            return <DashboardV2Screen onNavigate={handleTabNavigate} onBalancePress={handleOpenBalanceDetail} onInvestmentPress={handleOpenInvestmentDeposit} onCardPress={handleOpenCardDetail} />;
          case 'transactionDetail':
            if (selectedCardBalance && selectedTransaction) {
              return (
                <TransactionDetailScreen
                  balance={selectedCardBalance}
                  transaction={selectedTransaction}
                  onBack={() => {
                    setSelectedTransaction(null);
                    setCurrentTab(transactionOrigin);
                  }}
                  onNavigate={handleTabNavigate}
                />
              );
            }
            return <DashboardV2Screen onNavigate={handleTabNavigate} onBalancePress={handleOpenBalanceDetail} onInvestmentPress={handleOpenInvestmentDeposit} onCardPress={handleOpenCardDetail} />;
          case 'cardInfo':
            return (
              <CardInfoScreen
                cardId={selectedCardBalance?.wallet_address ?? ''}
                isBlocked={cardDetail?.data?.blocked === true}
                onBack={() => setCurrentTab('cardDetail')}
                onNavigate={handleTabNavigate}
                onBlockRequest={handleOpenCardBlock}
              />
            );
          case 'cardBlock':
            return (
              <CardBlockScreen
                onBack={goBack}
                onNavigate={handleTabNavigate}
                cardId={selectedCardBalance?.wallet_address ?? ''}
                mode={cardBlockMode}
              />
            );
          case 'operations':
            if (selectedBalance) {
              return (
                <OperationsScreen
                  initialTab={operationsInitialTab}
                  balance={selectedBalance}
                  cardBalance={selectedCardBalance}
                  cardDetail={cardDetail}
                  onBack={goBack}
                  onNavigate={handleTabNavigate}
                  onTransferSuccess={handleTransferSuccess}
                  onScanQR={handleOpenQRScanner}
                  scannedAddress={scannedAddress}
                  onClearScannedAddress={() => setScannedAddress(null)}
                  withdrawPersistedNetworkId={withdrawNetworkId}
                  onWithdrawNetworkChange={setWithdrawNetworkId}
                  onTopUpSuccess={() => setCurrentTab('transferSuccess')}
                  onApplyCardSubmitted={handleCardApplicationSubmitted}
                  repeatRequest={repeatRequest}
                  exchangePreset={exchangePreset}
                />
              );
            }
            return <DashboardV2Screen onNavigate={handleTabNavigate} onBalancePress={handleOpenBalanceDetail} onInvestmentPress={handleOpenInvestmentDeposit} onCardPress={handleOpenCardDetail} />;
          case 'qrScanner':
            return (
              <QRScannerScreen
                onScan={handleQRScan}
                onClose={handleCloseQRScanner}
              />
            );
          case 'transferSuccess':
            return (
              <TransferSuccessScreen
                onBackToDashboard={handleBackToDashboard}
                onNavigate={handleTabNavigate}
              />
            );
          case 'reports':
            return (
              <ReportsScreen
                onBack={reportsOrigin !== 'dashboard' ? () => setCurrentTab(reportsOrigin) : undefined}
                onNavigate={handleTabNavigate}
                onOpenPdfPreview={handleOpenPdfPreview}
              />
            );
          case 'allAssets':
            return (
              <AllAssetsScreen
                onBack={() => setCurrentTab('dashboard')}
                onNavigate={handleTabNavigate}
                onBalancePress={handleOpenBalanceDetail}
                onCardPress={handleOpenCardDetail}
                onInvestmentPress={handleOpenInvestmentDeposit}
                onStockPress={(stock) => openTokenDetail({ stock }, 'allAssets')}
              />
            );
          case 'popularTokens':
            return (
              <PopularTokensScreen
                onBack={() => setCurrentTab('dashboard')}
                onNavigate={handleTabNavigate}
                onTokenPress={(token) => openTokenDetail({ token }, 'popularTokens')}
                onStockPress={(stock) => openTokenDetail({ stock }, 'popularTokens')}
              />
            );
          case 'tokenDetail':
            if (selectedMarketStock) {
              return (
                <TokenDetailScreen
                  key={selectedMarketStock.id}
                  stock={selectedMarketStock}
                  onBack={() => setCurrentTab(tokenDetailOrigin)}
                  onNavigate={handleTabNavigate}
                  onBuy={(stock) => handleOpenMarketExchange({ stock }, 'buy')}
                  onSell={(stock) => handleOpenMarketExchange({ stock }, 'sell')}
                />
              );
            }
            if (selectedMarketToken) {
              return (
                <TokenDetailScreen
                  key={selectedMarketToken.id}
                  token={selectedMarketToken}
                  onBack={() => setCurrentTab(tokenDetailOrigin)}
                  onNavigate={handleTabNavigate}
                  onBuy={(token) => handleOpenMarketExchange({ token }, 'buy')}
                  onSell={(token) => handleOpenMarketExchange({ token }, 'sell')}
                />
              );
            }
            return <PopularTokensScreen
              onBack={() => setCurrentTab('dashboard')}
              onNavigate={handleTabNavigate}
              onTokenPress={(token) => openTokenDetail({ token }, 'popularTokens')}
              onStockPress={(stock) => openTokenDetail({ stock }, 'popularTokens')}
            />;
          case 'notifications':
            return (
              <NotificationsScreen
                onBack={() => setCurrentTab('dashboard')}
                onNavigate={handleTabNavigate}
              />
            );
          case 'openDeposit':
            return (
              <OpenDepositScreen
                balance={selectedBalance}
                onBack={() => setCurrentTab(openDepositOrigin)}
                onNavigate={handleTabNavigate}
                onDepositSuccess={() => setCurrentTab('dashboard')}
              />
            );
          case 'products':
            return (
              <ProductsScreen
                onNavigate={handleTabNavigate}
                onOpenDeposit={() => {
                  setOpenDepositOrigin('products');
                  handleTabNavigate('openDeposit');
                }}
              />
            );
          case 'orderDetail':
            return selectedRequest ? (
              <OrderDetailScreen
                request={selectedRequest}
                // Back to where the order was opened from — the Orders list
                // or the operation it produced — as the hardware button.
                onBack={goBack}
                onRepeat={handleRepeatRequest}
                onReceipt={handleOpenRequestReceipt}
                onSupport={handleRequestSupport}
                onNavigate={handleTabNavigate}
              />
            ) : renderHistory('orders');
          case 'history':
          case 'orders':
            return renderHistory(currentTab === 'orders' ? 'orders' : 'transactions');
          case 'historyDetail':
            if (selectedHistoryRecord) {
              return (
                <TransactionHistoryDetailScreen
                  record={selectedHistoryRecord}
                  onBack={() => {
                    setSelectedHistoryRecord(null);
                    setCurrentTab(historyDetailOrigin);
                  }}
                  onNavigate={handleTabNavigate}
                  onOpenRequest={handleOpenRequestFromHistory}
                />
              );
            }
            return renderHistory('transactions');
          case 'support':
            return (
              <SupportFlowScreen
                initialRoute={supportInitialRoute}
                onNavigate={handleTabNavigate}
              />
            );
          case 'profile':
            return (
              <ProfileScreen
                onBack={() => setCurrentTab(profileOrigin)}
                onNavigate={handleTabNavigate}
                onChangePassword={handleGoToChangePassword}
                onChangePIN={handleGoToChangePIN}
              />
            );
          case 'changePassword':
            return (
              <ChangePasswordScreen
                onBack={() => setCurrentTab('profile')}
                onNavigate={handleTabNavigate}
                onPasswordChanged={handlePasswordChanged}
              />
            );
          case 'changePIN':
            return (
              <ChangePINScreen
                onBack={() => setCurrentTab('profile')}
              />
            );
          case 'userSessions':
            return (
              <UserSessionsScreen
                onBack={() => setCurrentTab('profile')}
              />
            );
          case 'pinSetup':
            return (
              <PINSetupScreen
                onComplete={async (pin) => {
                  await handlePINSetupComplete(pin);
                  setCurrentTab('profile');
                }}
                onSkip={() => setCurrentTab('profile')}
              />
            );
          case 'noCard':
            return (
              <NoCardScreen
                onBack={() => setCurrentTab('dashboard')}
                onNavigate={(s) => setCurrentTab(s as TabScreen)}
                onSubmitted={handleCardApplicationSubmitted}
              />
            );
          default:
            // Fallthrough log: complements the handleTabNavigate guard.
            // When this fires it usually means a child tried onNavigate('foo')
            // with an unknown screen name and we silently landed on the dash.
            console.warn(`[App] Render fallthrough for currentTab="${currentTab}" — rendering dashboard`);
            return <DashboardV2Screen onNavigate={handleTabNavigate} />;
        }
      default:
        return (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={COLORS.brandOrange} />
          </View>
        );
    }
  };

  const isMainScreen = currentScreen === 'main';
  const isPinUnlockShown = isAuthenticated && isPINSetup && isLocked;
  const isInitialPinSetup = isAuthenticated && !isPINSetupCompleted && isMainScreen;
  const isPinRelatedScreen = (isMainScreen && currentTab === 'pinSetup') || isPinUnlockShown || isInitialPinSetup;
  const isFirstLoginSecurityScreen =
    isAuthenticated &&
    isPINSetupCompleted &&
    isMainScreen &&
    (firstLoginSecurityStep === 'biometrics' || firstLoginSecurityStep === 'password');

  const isMainNonPin = isMainScreen && !isPinRelatedScreen && !isFirstLoginSecurityScreen;
  const lightBgTabs: TabScreen[] = ['allAssets', 'popularTokens', 'tokenDetail', 'changePassword', 'changePIN', 'userSessions', 'transactionDetail', 'historyDetail', 'profile'];
  const isLightBgTab = isMainNonPin && lightBgTabs.includes(currentTab);
  const isBannerVisible = bannerData != null && !bannerDismissed;
  const safeAreaBg = isBannerVisible ? COLORS.primaryNavy : (isMainNonPin && !isLightBgTab) ? COLORS.primaryNavy : COLORS.white;
  const statusBarStyle = (isMainNonPin && !isLightBgTab) || isBannerVisible ? 'light-content' : 'dark-content';
  useWebStatusBarColor(safeAreaBg);

  // If PDF preview is open, show it as an overlay
  if (pdfDocument) {
    return (
      <View style={styles.container}>
        <PDFPreviewScreen
          document={pdfDocument}
          onClose={handleClosePdfPreview}
        />
      </View>
    );
  }

  // iOS main screens: use a split background so the top safe area (status bar/notch)
  // keeps COLORS.primaryNavy while the bottom safe area (home indicator) shows COLORS.pureLight.
  // A transparent absoluteFill SafeAreaView lays on top so its inset areas let the
  // split background show through rather than the SafeAreaView's own background colour.
  // The web gets the same: an iPhone home-screen app has the same notch and home indicator.
  if ((Platform.OS === 'ios' || Platform.OS === 'web') && isMainNonPin && !isLightBgTab) {
    return (
      <View style={styles.iosSplitRoot} {...panResponder.panHandlers}>
        {/* Top half → navy (covers top safe area inset) */}
        <View style={styles.iosSplitTop} />
        {/* Bottom half → pureLight (covers bottom safe area inset) */}
        <View style={styles.iosSplitBottom} />
        <SafeAreaView style={[StyleSheet.absoluteFill, styles.iosSafeArea, styles.webTopInset]}>
          <StatusBar barStyle="light-content" />
          <ImpersonationBanner />
          {renderScreen()}
        </SafeAreaView>
      </View>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: safeAreaBg }, styles.webTopInset]} {...panResponder.panHandlers}>
      <StatusBar
        barStyle={statusBarStyle}
        backgroundColor={safeAreaBg}
      />
      <ImpersonationBanner />
      {renderScreen()}
    </SafeAreaView>
  );
}

function App(): React.JSX.Element {
  return (
    <AuthProvider>
      <PINProvider>
        <ChatProvider>
          <HiddenAccountsProvider>
            <SnackbarProvider>
              <AppContent />
            </SnackbarProvider>
          </HiddenAccountsProvider>
        </ChatProvider>
      </PINProvider>
    </AuthProvider>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.primaryNavy,
    // Not on the web: there SafeAreaView pads with env(safe-area-inset-*), and a paddingTop here would
    // override the top inset, putting the headers under the status bar of a home-screen app.
    ...(Platform.OS === 'web'
      ? null
      : { paddingTop: Platform.OS === 'android' ? StatusBar.currentHeight === undefined ? 0 : StatusBar.currentHeight - 25 : 0 }),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: COLORS.white,
  },
  // iOS split-background safe area helpers
  iosSplitRoot: {
    flex: 1,
  },
  iosSplitTop: {
    flex: 1,
    backgroundColor: COLORS.primaryNavy,
  },
  iosSplitBottom: {
    flex: 1,
    // Match the body bg of the navy-top screens (NotificationsScreen,
    // ReportsScreen, etc.) — they use COLORS.pureLight for the rounded
    // content card. Was COLORS.white, which painted the iOS home-indicator
    // gutter pure white and showed a visible bottom strip on screens
    // without a TabBar overlay (e.g. Notifications). The comment above
    // already said "shows COLORS.pureLight" — code now matches the intent.
    backgroundColor: COLORS.pureLight,
  },
  iosSafeArea: {
    backgroundColor: 'transparent',
  },
  // Web: in an iOS 26+ home-screen app the system fades the top of the page
  // well below the status bar (to ~98 pt on iPhone 18 Pro), dimming the
  // headers. web/standaloneViewport.ts sets the extra gap there; 0 elsewhere.
  webTopInset: Platform.OS === 'web'
    ? { paddingTop: 'calc(env(safe-area-inset-top) + var(--standalone-top-extra, 0px))' }
    : {},
});

export default App;
